import { supabase } from './supabase';
import { SubscriptionStatus } from '../types/subscription';

export async function createCheckoutSession(mode: 'one-time' | 'subscription', priceId: string, customSuccessUrl?: string): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');

    // Map our internal modes to Stripe's accepted modes
    const stripeMode = mode === 'one-time' ? 'payment' : 'subscription';
    
    // Check if there's pending card data in localStorage
    const pendingCardData = localStorage.getItem('cardible_pending_card');
    const hasPendingCard = !!pendingCardData;
    
    // Build a more informative success URL with template variables
    let successUrl = customSuccessUrl;
    if (!successUrl) {
      // Get the base URL and add parameters
      const baseUrl = `${window.location.origin}${window.location.pathname}`;
      
      // Add important context parameters
      const timestamp = Date.now();
      successUrl = `${baseUrl}?success=true&session_id={CHECKOUT_SESSION_ID}&timestamp=${timestamp}`;
      
      // Add pendingCard flag if there's pending card data
      if (hasPendingCard) {
        successUrl += '&pendingCard=true';
      }
      
      // Add mode information for better tracking
      successUrl += `&mode=${mode}`;
    }
    
    // Log the success URL pattern for debugging (without session ID)
    console.log('Stripe checkout success URL pattern:', 
      successUrl.replace('{CHECKOUT_SESSION_ID}', '[SESSION_ID_PLACEHOLDER]'));

    const { data, error } = await supabase.functions.invoke('stripe-create-checkout', {
      body: { 
        mode: stripeMode,
        priceId,
        userId: session.user.id,
        successUrl: successUrl,
        cancelUrl: window.location.href
      }
    });

    if (error) throw error;
    if (!data || !data.url) throw new Error('No checkout URL returned');

    // Redirect to Stripe checkout
    window.location.href = data.url;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
}

// Add function to handle return from Stripe Checkout
export async function handleStripeReturn(): Promise<{ 
  success: boolean; 
  sessionId?: string; 
  status?: any;
  pendingCard?: boolean;
  mode?: string;
  timestamp?: number;
  isSubscription?: boolean;
}> {
  try {
    // Check if we have stripe session parameters
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const success = params.get('success') === 'true';
    const pendingCard = params.get('pendingCard') === 'true';
    const mode = params.get('mode') || 'one-time';
    const timestamp = params.get('timestamp') ? parseInt(params.get('timestamp')!) : undefined;
    const isSubscription = mode === 'subscription';
    
    console.log('Stripe return parameters:', { 
      success, 
      sessionId: sessionId ? sessionId.substring(0, 10) + '...' : undefined,
      pendingCard,
      mode,
      timestamp 
    });
    
    // If no session ID, this isn't a valid return from Stripe
    if (!sessionId) {
      return { success: false };
    }
    
    // Verify the session status with Stripe
    const { data, error } = await supabase.functions.invoke('stripe-check-session', {
      body: { sessionId }
    });

    if (error) {
      console.error('Error checking Stripe session:', error);
      throw error;
    }

    // Include all the context in the return value
    return { 
      success: data?.success || false, 
      sessionId, 
      status: data?.status,
      pendingCard,
      mode,
      timestamp,
      isSubscription
    };
  } catch (error) {
    console.error('Error handling Stripe return:', error);
    return { success: false };
  }
}

export async function redirectToCustomerPortal() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');

    // The Edge Function will create a customer if one doesn't exist
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-customer-portal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        userId: session.user.id,
        userEmail: session.user.email, // Pass email directly
        returnUrl: window.location.origin
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
      throw new Error(errorData.error || 'Failed to create portal session');
    }

    const data = await response.json();
    if (data.url) {
      window.location.href = data.url;
    } else {
      throw new Error('No URL returned from portal session creation');
    }
  } catch (error) {
    console.error('Error redirecting to customer portal:', error);
    throw error;
  }
}

export async function getSubscriptionStatus() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return {
        subscription: {},
        freeCards: 0,
        cardsUsedThisMonth: 0
      };
    }

    // Get subscription status using RPC function
    const { data: subscription, error: subscriptionError } = await supabase
      .rpc('get_user_subscription', { user_id: session.user.id });

    if (subscriptionError) {
      console.error('Error fetching subscription:', subscriptionError);
    }

    // Get credit updates
    const { data: creditUpdates, error: creditError } = await supabase
      .from('credit_updates')
      .select('credits_data')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });

    if (creditError) {
      console.error('Error fetching credits:', creditError);
    }

    // Calculate total credits and used credits
    let totalCredits = 0;
    let usedCredits = 0;

    creditUpdates?.forEach(update => {
      if (Array.isArray(update.credits_data)) {
        update.credits_data.forEach((credit: any) => {
          if (credit.type === 'addition') {
            totalCredits += credit.amount;
          } else if (credit.type === 'deduction') {
            usedCredits += credit.amount;
          }
        });
      }
    });

    // Get all subscription products to build the subscription status
    const { data: subscriptionProducts, error: productsError } = await supabase
      .rpc('get_subscription_plans');

    if (productsError) {
      console.error('Error fetching subscription products:', productsError);
    }

    // Build subscription status object using product names and check if subscription is active
    const subscriptionStatus: { [key: string]: boolean } = {};
    const subscriptionDetails = subscription && Object.keys(subscription).length > 0 ? subscription : undefined;
    const isActive = subscriptionDetails?.status === 'active' || subscriptionDetails?.status === 'trialing';
    
    subscriptionProducts?.forEach((product: { id: string; name: string }) => {
      // Set subscription status based on product ID and active status
      subscriptionStatus[product.name.toLowerCase()] = isActive && subscriptionDetails?.product_id === product.id;
    });

    return {
      subscription: subscriptionStatus,
      freeCards: totalCredits,
      cardsUsedThisMonth: usedCredits,
      subscriptionDetails
    };
  } catch (error) {
    console.error('Error getting subscription status:', error);
    return {
      subscription: {},
      freeCards: 0,
      cardsUsedThisMonth: 0,
      subscriptionDetails: undefined
    };
  }
}

// Add function to check session status
export async function checkSessionStatus(sessionId: string) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-check-session?session_id=${sessionId}`, {
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to check session status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error checking session status:', error);
    throw error;
  }
}

export function hasActiveSubscription(status?: SubscriptionStatus | null) {
  if (!status?.subscription) return false;
  return Object.values(status.subscription).some(isActive => isActive === true);
} 